<template>
  <div>
    <LazyDeliveryOptions
      v-if="!isCheckout"
      data-test="delivery-options-container"
      :initial-delivery-id="getSelectedDeliveryId"
      @input="inputChange"
      @open-selector="openSelector"
    />
    <LazyDeliveryMethodFlyout
      v-else-if="!isStoreFlyoutVisible && !isZipCodeFlyoutVisible"
      data-test="delivery-method-flyout"
      :item-delivery-id="currentDeliveryId"
      @update:item-delivery-id="currentDeliveryId = $event"
      @save-item-delivery-id="saveItemDeliveryId"
      @open-selector="openSelector"
      @close="close"
    />
    <LazyZipCodeFlyout
      v-if="isZipCodeFlyoutVisible"
      :delivery-id="selectorDeliveryId"
      @close="closeZipCodeFlyout"
      @zip-code-selected="saveItemDeliveryId"
    />
    <LazyStoreFlyout
      v-if="isStoreFlyoutVisible"
      :delivery-id="selectorDeliveryId"
      @close="isStoreFlyoutVisible = false"
      @store-selected="saveItemDeliveryId"
    />
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { useCartStore, useDeliveryStore } from '@/stores';
import { CartItem } from '@/stores/cart';
import { DeliveryId, DeliveryObject } from '@/config/delivery-config';

type Props = {
  isCheckout?: boolean;
  items?: CartItem[];
  deliveryOptions?: DeliveryObject[];
};

const props = withDefaults(defineProps<Props>(), {
  isCheckout: false,
  items: () => [],
  deliveryOptions: () => [],
});

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const config = useRuntimeConfig();

const { getSelectedDeliveryId } = storeToRefs(useDeliveryStore());
const { setSelectedDeliveryId } = useDeliveryStore();
const { updateItem, updateItems } = useCartStore();

const isStoreFlyoutVisible = ref(false);
const isZipCodeFlyoutVisible = ref(false);
const currentDeliveryId = ref('');
const selectorDeliveryId = ref('');

const closeZipCodeFlyout = (zipCode: boolean) => {
  // fallback to cartitem delivery id
  if (props.isCheckout && !zipCode) {
    currentDeliveryId.value = '';
    if (props.items.every((item, _, arr) => item.deliveryId === arr[0].deliveryId)) {
      currentDeliveryId.value = props.items[0].deliveryId;
    }
  }
  isZipCodeFlyoutVisible.value = false;
};

const close = () => {
  emit('close');
};

const openSelector = (option: DeliveryObject) => {
  selectorDeliveryId.value = option.id;
  if (option.requiresStore) {
    isStoreFlyoutVisible.value = true;
  } else if (option.requiresZipCode) {
    isZipCodeFlyoutVisible.value = true;
  }
};

const inputChange = (deliveryId: string) => {
  setSelectedDeliveryId(deliveryId);
};

const saveItemDeliveryId = (_: any, deliveryId: DeliveryId) => {
  if (!props.isCheckout) return;
  if (deliveryId !== currentDeliveryId.value) return;
  if (props.items.length === 1) {
    updateItem(
      {
        ...props.items[0],
        deliveryId: currentDeliveryId.value,
      },
      config.public.gtm.CART_LIST,
    );
  } else {
    updateItems(
      props.items.map((item) => ({
        ...item,
        deliveryId: currentDeliveryId.value,
      })) as CartItem[],
    );
  }
  close();
};

onMounted(() => {
  if (props.isCheckout && props.items.every((item, _, arr) => item.deliveryId === arr[0].deliveryId)) {
    currentDeliveryId.value = props.items[0].deliveryId;
  }
});
</script>

<script lang="ts">
export default defineComponent({
  name: 'SelectDelivery',
});
</script>
